import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import services from '../../services';

// "Also assigned" people of a ticket (task_assignees). The main assignee stays in AssignDropdown.
// Every person here sees the ticket on the board and can open it, like the main assignee.
const avatarStyle = { background: 'linear-gradient(135deg, #1a7d3e 0%, #34a85a 100%)' };

const ExtraAssignees = ({ currentTaskId, selectedBoardId, sharedBoardIds, listKey, mainUserId, mainKey, onChange }) => {
    const [assignees, setAssignees] = useState([]);
    const [boardUsers, setBoardUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    // main assignee from the server (taskItem is empty when the ticket is opened by link)
    const [serverMainUserId, setServerMainUserId] = useState(null);
    // admin / board manager / main assignee may change the list (server decides); others only see it
    const [canManage, setCanManage] = useState(false);
    // message of the "no access to this list" popup ('' = closed)
    const [noAccess, setNoAccess] = useState('');
    const boxRef = useRef(null);
    const addRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState({});

    // Fixed position inside the viewport (the ticket panel is narrow), same idea as AssignDropdown
    const openList = () => {
        const rect = addRef.current?.getBoundingClientRect();
        if (rect) {
            const width = 260;
            const height = 280;
            const up = window.innerHeight - rect.bottom < height && rect.top > height;
            setDropdownStyle({
                position: 'fixed',
                top: up ? rect.top - height : rect.bottom + 4,
                left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
                width,
                zIndex: 9999,
            });
        }
        setOpen((v) => !v);
    };

    // Users of the ticket's board + of every board it is shared to
    const boardKey = [selectedBoardId, ...(sharedBoardIds || [])].filter(Boolean).join(',');

    // Reload when the ticket, its board/list or its shares change: people who can't see the ticket any
    // more (share removed, ticket moved to a list they have no access to) drop out of "Also assigned".
    const shownRef = useRef({ taskId: null, list: [] });
    const mainKeyRef = useRef(mainKey);
    useEffect(() => {
        if (!currentTaskId) return;
        let alive = true;
        // main assignee just picked: its save runs right after the pick, so read a moment later
        const wait = mainKeyRef.current !== mainKey ? 1200 : 0;
        mainKeyRef.current = mainKey;
        new Promise((r) => setTimeout(r, wait))
            .then(() => (alive ? services.taskAssignee.GET(currentTaskId) : null))
            .then((res) => {
                if (!res) return;
                if (!alive) return;
                const next = res?.data?.data || [];
                const before = shownRef.current;
                const main = res?.data?.mainUserId || null;
                if (before.taskId === currentTaskId) {
                    // a person who became the main assignee did not lose access — no popup for them
                    const gone = before.list.filter((a) => !next.some((n) => n.id === a.id) && a.id !== main);
                    if (gone.length) {
                        setNoAccess(
                            `${gone.map((a) => a.name || a.email).join(', ')} ${gone.length > 1 ? 'were' : 'was'} removed from "Also assigned" — no access to this ticket's board or list any more.`
                        );
                    }
                }
                shownRef.current = { taskId: currentTaskId, list: next };
                setAssignees(next);
                setServerMainUserId(res?.data?.mainUserId || null);
                setCanManage(res?.data?.canManage === true);
            })
            .catch(() => alive && setAssignees([]));
        return () => {
            alive = false;
        };
    }, [currentTaskId, boardKey, listKey, mainKey]);
    useEffect(() => {
        const ids = [...new Set(boardKey.split(',').filter(Boolean))];
        if (!ids.length) return;
        Promise.all(
            ids.map((id) =>
                services.userassignboard
                    .GET_ASSIGN_USER_BY_BOARD_ID(id, '', true)
                    .then((res) => res?.data?.data || [])
                    .catch(() => []),
            ),
        ).then((lists) => {
            const rows = lists.flat();
            setBoardUsers(Array.from(new Map(rows.filter((r) => r.User).map((r) => [r.userId, r.User])).values()));
        });
    }, [boardKey]);

    useEffect(() => {
        const close = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const save = async (next) => {
        if (!currentTaskId) {
            toast.error('Please select a board first');
            return;
        }
        setSaving(true);
        try {
            const res = await services.taskAssignee.SET(currentTaskId, next.map((u) => u.id));
            const saved = res?.data?.data || [];
            shownRef.current = { taskId: currentTaskId, list: saved };
            setAssignees(saved);
            if (onChange) onChange(saved);
        } catch (err) {
            const data = err?.response?.data;
            // Person can't see the ticket's list → popup instead of a toast (nothing was saved)
            if (data?.code === 'no_list_access') setNoAccess(data.message);
            else toast.error(data?.message || 'Could not save the assignees');
        } finally {
            setSaving(false);
        }
    };

    const taken = new Set([mainUserId, serverMainUserId, ...assignees.map((a) => a.id)].filter(Boolean));
    const options = boardUsers.filter(
        (u) =>
            !taken.has(u.id) &&
            ((u.name || '').toLowerCase().includes(search.toLowerCase()) ||
                (u.email || '').toLowerCase().includes(search.toLowerCase())),
    );

    return (
        <div
            className="relative w-full text-left"
            ref={boxRef}
            data-testid="extra-assignees"
            title={canManage ? undefined : 'Only an admin, a manager of this board, the main assignee or a user with "Assignees" switched on for this board (User management → Assign board) can change who is assigned'}
        >
            <div className="flex flex-wrap items-center gap-1">
                <span className="text-gray-500" style={{ fontSize: 11 }}>
                    Also assigned:
                </span>
                {assignees.length === 0 && (
                    <span className="text-gray-400" style={{ fontSize: 11 }}>
                        nobody
                    </span>
                )}
                {assignees.map((a) => (
                    <span
                        key={a.id}
                        className="flex items-center gap-1 rounded-full border px-1"
                        style={{ fontSize: 11, borderColor: '#cfe8d6', background: '#f3faf5' }}
                        title={a.email || a.name}
                    >
                        <span className="w-4 h-4 rounded-full text-white flex items-center justify-center font-bold" style={{ ...avatarStyle, fontSize: 9 }}>
                            {(a.name || a.email || '?').charAt(0).toUpperCase()}
                        </span>
                        {a.name || a.email}
                        {canManage && <button
                            type="button"
                            aria-label={`Remove ${a.name || a.email}`}
                            disabled={saving}
                            onClick={() => save(assignees.filter((x) => x.id !== a.id))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '0 2px', lineHeight: 1 }}
                        >
                            ×
                        </button>}
                    </span>
                ))}
                {canManage && <button
                    type="button"
                    ref={addRef}
                    disabled={saving}
                    onClick={openList}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a7d3e', fontWeight: 600, fontSize: 11, padding: 0 }}
                >
                    + Add person
                </button>}
            </div>
            {open && (
                <div className="search-assign p-2 bg-white rounded-lg shadow-lg" style={dropdownStyle}>
                    <input
                        type="text"
                        placeholder="Search by name"
                        className="inputField-text mb-2 w-full"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-no-popup
                        style={{ backgroundColor: 'white' }}
                    />
                    <ul className="assign-box rounded-2xl max-h-60 overflow-y-auto w-full">
                        {options.length === 0 ? (
                            <li className="p-2 text-gray-400">No users found</li>
                        ) : (
                            options.map((u) => (
                                <li
                                    key={u.id}
                                    className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer"
                                    data-no-popup
                                    onClick={() => {
                                        setOpen(false);
                                        setSearch('');
                                        save([...assignees, u]);
                                    }}
                                >
                                    <span className="text-white w-6 h-6 flex items-center justify-center rounded-full font-bold" style={avatarStyle}>
                                        {(u.name || '?').charAt(0).toUpperCase()}
                                    </span>
                                    {u.name}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
            {noAccess && (
                <div
                    data-no-popup
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="no-list-access-title"
                        data-testid="no-list-access-popup"
                        style={{ background: '#fff', borderRadius: 10, padding: '18px 20px', width: 360, maxWidth: 'calc(100vw - 32px)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
                    >
                        <div id="no-list-access-title" style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: '#92400e' }}>
                            No access
                        </div>
                        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.45 }}>{noAccess}</div>
                        <div style={{ textAlign: 'right', marginTop: 14 }}>
                            <button
                                type="button"
                                onClick={() => setNoAccess('')}
                                style={{ background: '#1a7d3e', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600, cursor: 'pointer' }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExtraAssignees;
